import React from "react";

export const PersistentStateContext = React.createContext(new Map());
PersistentStateContext.displayName = "PersistentStateContext";

type StateSetter<T> = (newState: T | ((oldState: T) => T)) => void;

export default function usePersistentState<T>(key: unknown, initialState: T | (() => T)): [T, StateSetter<T>] {
	const ctx = React.useContext(PersistentStateContext);
	const [state, setState] = React.useState<T>(ctx.has(key) ? ctx.get(key) : initialState);

	React.useEffect(() => {
		ctx.set(key, state);
	}, [state, ctx, key]);

	return [state, setState];
}

export function usePersistentRef<T>(key: unknown, initialValue: T): React.MutableRefObject<T> {
	const ctx = React.useContext(PersistentStateContext);
	const ref = React.useRef(ctx.has(key) ? ctx.get(key) : initialValue);

	React.useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			ctx.set(key, ref.current);
		};
	}, [key, ref, ctx]);

	return ref;
}
